import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import axios from "axios";
import { staffMe } from "@/utils/staffAuth";
import { setAdminAuthenticated } from "@/utils/adminAuth";
import LiveCheckinBanner from "@/components/admin/LiveCheckinBanner";
import IdleLockScreen from "@/components/admin/IdleLockScreen";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Wraps every /admin/* route. Calls /api/auth/me on mount; if no valid
 * 8-hour session, redirects to /staff/login with ?next=<current path> so
 * the user lands back where they came from after signing in.
 *
 * When the caller is an owner, we ALSO synchronously fetch the live
 * runtime ADMIN_PASSCODE via /api/admin/passcode-for-owner and stash it
 * in sessionStorage BEFORE rendering the admin children. Without this
 * step there was a race: AdminHubPage's `useEffect`s (LMC pending badge,
 * points activity, KPIs) fired on mount with no passcode → the backend
 * rejected them with 403 "Invalid passcode" → a red toast surfaced even
 * though the owner was correctly logged in. (Fix: Karine, Jul 1 2026.)
 *
 * Session lifetime is enforced server-side (8h JWT access token + 8h trust
 * cookie). This gate is just the UX layer that keeps unauthed users out.
 *
 * Optional `requireOwner` prop — set true for routes that only Karine/Marc
 * should ever see (e.g. /admin/head-office).
 */
export default function AdminAuthGate({ children, requireOwner = false }) {
  const location = useLocation();
  const [state, setState] = useState({ status: "checking", user: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const u = await staffMe();
      if (cancelled) return;
      if (!u) {
        setState({ status: "denied", user: null });
        return;
      }
      if (requireOwner && u.role !== "owner") {
        setState({ status: "denied-role", user: u });
        return;
      }
      // Owner path — hydrate the passcode BEFORE unblocking the children.
      // Any admin page whose mount-time useEffect calls `${API}/admin/…?passcode=…`
      // will therefore see the correct value in sessionStorage. Silent on
      // failure (e.g. injectors on shared admin pages) — the individual page
      // will surface its own error if any endpoint really needs the passcode.
      if (u.role === "owner") {
        try {
          const { data } = await axios.get(
            `${API}/admin/passcode-for-owner`,
            { withCredentials: true }
          );
          if (data?.passcode) setAdminAuthenticated(data.passcode);
        } catch {
          // Non-fatal — page still loads, but admin API calls that need
          // the passcode will fail loudly per-page.
          console.warn("[AdminAuthGate] owner passcode prefetch failed");
        }
      }
      if (cancelled) return;
      setState({ status: "ok", user: u });
    })();
    return () => { cancelled = true; };
  }, [requireOwner, location.pathname]);

  if (state.status === "checking") {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-black text-zinc-400"
        data-testid="admin-auth-gate-loading"
      >
        <div className="text-sm">Verifying session…</div>
      </div>
    );
  }

  if (state.status === "denied") {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/staff/login?next=${next}`} replace />;
  }

  if (state.status === "denied-role") {
    // Logged in but wrong role — send them to the role-appropriate landing
    return <Navigate to="/staff" replace />;
  }

  return (
    <>
      {children}
      <LiveCheckinBanner />
      <IdleLockScreen />
    </>
  );
}
